import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useFadeIn } from '@/hooks/useFadeIn';
import { services, contactInfo } from '@/mocks/homeData';
import { serviceDetailData, ServiceDetailData } from '@/mocks/serviceData';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_ACTION_URL = 'https://formspree.io/f/mgaoyonp';

const clinicPhotos = [
  {
    src: 'https://storage.readdy-site.link/project_files/d597c584-8282-4ae1-8b44-377681ee7a13/6251e436-a5f4-4143-8003-0eb9b0dc4f23_compressed_IMG_3641.JPG.webp',
    alt: 'Recepção do consultório odontológico Salomão Cunha - ambiente acolhedor e sofisticado na Faria Lima',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/d597c584-8282-4ae1-8b44-377681ee7a13/0dc8b2bb-a8ea-490b-8aeb-a3d916540d58_compressed_IMG_3613.JPG.webp',
    alt: 'Consultório odontológico moderno com vista para a cidade de São Paulo',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/d597c584-8282-4ae1-8b44-377681ee7a13/d77f738a-63b8-45c7-a6b8-248622055ab1_compressed_IMG_3675.webp',
    alt: 'Sala de atendimento odontológico com equipamentos modernos e tecnologia 3D',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/d597c584-8282-4ae1-8b44-377681ee7a13/07a7ae0a-bcd6-4998-ad3c-f94d26cc2b90_compressed_IMG_3651.JPG.webp',
    alt: 'Brinquedoteca acolhedora para crianças no consultório odontopediátrico',
  },
];

function ServiceHeroSection({ detail, service }: { detail: ServiceDetailData; service: typeof services[0] }) {
  return (
    <section className="relative w-full h-[420px] md:h-[550px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={detail.heroImage}
          alt={`${service.title} - Dra. Marihana Pasquali - Faria Lima, São Paulo`}
          className="w-full h-full object-cover object-center"
          title={`${service.title} na Faria Lima - Dra. Marihana Pasquali CRO-SP 172094`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-16">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 text-white/80 text-xs font-medium mb-4 tracking-wide">
            <i className={`${service.icon} text-white/60`}></i>
            {service.title}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4">
            {detail.heroTitle}
          </h1>
          <p className="text-white/80 text-sm md:text-base leading-relaxed max-w-lg font-light">
            {detail.heroSubtitle}
          </p>
        </div>
      </div>
    </section>
  );
}

function ClinicGallerySection() {
  const { ref, isVisible } = useFadeIn(0.1);
  return (
    <section ref={ref} className={`py-16 md:py-20 bg-background-50 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
            Nosso Espaço
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
            Tecnologia e conforto em cada detalhe
          </h2>
          <p className="text-foreground-500 text-sm mt-3 max-w-xl mx-auto">
            Consultório moderno, acolhedor e equipado com o que há de mais avançado em odontologia para sua segurança e bem-estar.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {clinicPhotos.map((photo, index) => (
            <div
              key={index}
              className="rounded-xl overflow-hidden img-zoom aspect-[4/3]"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceFAQSection({ faqs, serviceTitle }: { faqs: { question: string; answer: string }[]; serviceTitle: string }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, isVisible } = useFadeIn(0.1);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  };

  return (
    <section ref={ref} className={`py-20 md:py-24 bg-background-100 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}></script>
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <span className="text-xs uppercase tracking-[0.2em] text-accent-600 font-semibold mb-3 block">
            Dúvidas Frequentes
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
            Tire suas dúvidas sobre {serviceTitle}
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((item, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-xl overflow-hidden transition-all duration-300"
            >
              <button
                onClick={() => toggle(index)}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer"
              >
                <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 pr-4">
                  {item.question}
                </h3>
                <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${
                  openIndex === index
                    ? 'bg-primary-500 text-white rotate-45'
                    : 'bg-background-200 text-foreground-500'
                }`}>
                  <i className="ri-add-line text-lg"></i>
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${
                  openIndex === index ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="px-5 md:px-6 pb-5 md:pb-6 text-foreground-600 text-sm leading-relaxed">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCTASection({ serviceTitle }: { serviceTitle: string }) {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const { ref, isVisible } = useFadeIn(0.1);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      return;
    }

    setFormStatus('loading');

    try {
      const res = await fetch(FORM_ACTION_URL, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      if (res.ok) {
        setFormStatus('success');
        setFormMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        form.reset();
      } else {
        let errorMessage = 'Erro ao enviar. Tente novamente.';
        try {
          const data = await res.json();
          if (Array.isArray(data.errors) && data.errors.length) {
            errorMessage = data.errors.map((item: { message?: string }) => item.message || '').filter(Boolean).join(' ');
          }
        } catch { /* mantém mensagem padrão */ }
        setFormStatus('error');
        setFormMessage(errorMessage || 'Erro ao enviar. Tente novamente.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Erro de conexão. Tente novamente.');
    }
  };

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-background-50 fade-section ${isVisible ? 'is-visible' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <span className="text-xs uppercase tracking-[0.2em] text-primary-500 font-semibold mb-3 inline-block">
              Próximo passo
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              Pronto para transformar seu sorriso com {serviceTitle}?
            </h2>
            <p className="text-foreground-600 text-base leading-relaxed mb-8">
              Agende sua avaliação personalizada com a Dra. Marihana Pasquali. Descubra o plano ideal para você com tecnologia 3D e atendimento humanizado na Faria Lima.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <a
                href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Tenho%20interesse%20em%20${encodeURIComponent(serviceTitle)}.%20Gostaria%20de%20agendar%20uma%20avaliação.`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="whitespace-nowrap bg-[#25D366] hover:bg-[#20bd5a] text-white text-sm font-semibold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer text-center"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Agendar pelo WhatsApp
              </a>
              <a
                href={`tel:${contactInfo.phone}`}
                className="whitespace-nowrap border border-foreground-200 text-foreground-700 hover:border-foreground-400 text-sm font-medium px-8 py-4 rounded-full transition-all duration-300 cursor-pointer text-center"
              >
                <i className="ri-phone-line mr-2"></i>
                Ligar Agora
              </a>
            </div>

            <div className="flex items-center gap-4 justify-center lg:justify-start text-xs text-foreground-400">
              <span><i className="ri-shield-check-line mr-1 text-accent-500"></i> Dados protegidos</span>
              <span><i className="ri-time-line mr-1 text-accent-500"></i> Resposta em até 15 min</span>
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-1">Solicite sua avaliação</h3>
              <p className="text-foreground-500 text-sm mb-6">Preencha e retornaremos em breve para agendar seu horário.</p>

              <form onSubmit={handleSubmit} id="service-lead-form" className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nome" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      Nome completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      required
                      className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors placeholder:text-foreground-300"
                      placeholder="Seu nome"
                    />
                  </div>
                  <div>
                    <label htmlFor="whatsapp" className="block text-xs font-medium text-foreground-600 mb-1.5">
                      WhatsApp *
                    </label>
                    <input
                      type="tel"
                      id="whatsapp"
                      name="whatsapp"
                      required
                      className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors placeholder:text-foreground-300"
                      placeholder="(11) 9XXXX-XXXX"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors placeholder:text-foreground-300"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="interesse" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Tratamento de interesse *
                  </label>
                  <select
                    id="interesse"
                    name="interesse"
                    required
                    defaultValue={serviceTitle}
                    className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors cursor-pointer appearance-none"
                  >
                    <option value={serviceTitle}>{serviceTitle}</option>
                    {services.filter(s => s.title !== serviceTitle).map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                    <option value="outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="mensagem" className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Mensagem (opcional)
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    rows={3}
                    maxLength={500}
                    className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-950 outline-none focus:border-accent-400 transition-colors placeholder:text-foreground-300 resize-none"
                    placeholder="Conte-nos um pouco sobre o que você precisa..."
                  ></textarea>
                </div>

                <div className="antispam">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer"
                >
                  {formStatus === 'loading' ? 'Enviando...' : 'Solicitar Avaliação'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-accent-600 text-sm text-center font-medium">{formMessage}</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center">{formMessage}</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FadeSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useFadeIn(0.1);
  return (
    <div ref={ref} className={`${className} fade-section ${isVisible ? 'is-visible' : ''}`}>
      {children}
    </div>
  );
}

function ServiceSchema({ service, detail }: { service: typeof services[0]; detail: ServiceDetailData }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${service.title} na Faria Lima — Dra. Marihana Pasquali`,
    serviceType: service.title,
    description: service.shortDescription,
    image: detail.heroImage,
    provider: {
      '@type': 'Dentist',
      name: 'Dra. Marihana Pasquali',
      telephone: contactInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Av. Brigadeiro Faria Lima, 2355 - 6º andar',
        addressLocality: 'São Paulo',
        addressRegion: 'SP',
        postalCode: '01452-000',
        addressCountry: 'BR',
      },
    },
    areaServed: { '@type': 'City', name: 'São Paulo' },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function ServicePage() {
  const { slug } = useParams<{ slug: string }>();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <p className="text-foreground-600 text-lg">Serviço não encontrado.</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm font-medium mt-4 inline-block cursor-pointer">Voltar ao início</Link>
        </div>
      </div>
    );
  }

  const service = services.find((s) => s.id === slug);
  const detail = serviceDetailData[slug];

  if (!service || !detail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Serviço não encontrado</h1>
          <p className="text-foreground-600 mb-6">O serviço que você procura não está disponível.</p>
          <Link to="/" className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-8 py-3 rounded-full transition-all duration-300 cursor-pointer inline-block">
            Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  const descriptionParagraphs = detail.description.split('\n\n');

  return (
    <main className="min-h-screen bg-background-50">
      <ServiceSchema service={service} detail={detail} />
      <ServiceHeroSection detail={detail} service={service} />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <Breadcrumb
          items={[
            { label: 'Início', href: '/' },
            { label: 'Serviços', href: '/#servicos' },
            { label: service.title },
          ]}
        />
      </div>

      <FadeSection className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            <div className="w-full lg:w-5/12">
              <div className="rounded-2xl overflow-hidden img-zoom">
                <img
                  src={service.image}
                  alt={`${service.title} no consultório da Dra. Marihana Pasquali - Faria Lima, São Paulo`}
                  className="w-full rounded-2xl object-cover"
                  title={`${service.title} - Odontologia Premium na Faria Lima`}
                />
              </div>
            </div>
            <div className="w-full lg:w-7/12">
              <span className="text-xs uppercase tracking-[0.2em] text-accent-600 font-semibold mb-2 inline-block">
                Visão Geral
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6 leading-tight">
                {detail.headline}
              </h2>
              {descriptionParagraphs.map((paragraph, i) => (
                <p key={i} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </FadeSection>

      <FadeSection className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-accent-600 font-semibold mb-3 block">
              Para quem é indicado
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
              Este tratamento é ideal para você se...
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {detail.indications.map((item, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-xl p-5 flex items-start gap-3 card-hover"
              >
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-accent-50 rounded-full mt-0.5">
                  <i className="ri-check-line text-accent-600 text-xs"></i>
                </span>
                <p className="text-foreground-700 text-sm font-medium leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </FadeSection>

      <ClinicGallerySection />

      <FadeSection className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.2em] text-primary-500 font-semibold mb-3 block">
              Jornada do Tratamento
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
              Como funciona — passo a passo
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {detail.treatmentSteps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-background-100 rounded-2xl p-6 h-full transition-all duration-300 hover:bg-background-200/80 card-hover">
                  <span className="font-heading text-4xl font-bold text-accent-200 mb-4 block">
                    {step.step}
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < detail.treatmentSteps.length - 1 && (
                  <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-s-line text-foreground-300 text-xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </FadeSection>

      <FadeSection className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-accent-600 font-semibold mb-3 block">
              Benefícios Exclusivos
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
              Por que escolher {service.title} conosco
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {service.benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4">
                <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-50 rounded-xl">
                  <i className="ri-star-line text-accent-600"></i>
                </span>
                <p className="text-foreground-700 text-sm font-medium leading-snug pt-2">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </FadeSection>

      <ServiceFAQSection faqs={detail.serviceFaqs} serviceTitle={service.title} />

      <ServiceCTASection serviceTitle={service.title} />
    </main>
  );
}